import { normalizeUrl } from './normalizeUrl';
import { DATA_URL_REGEX, parseUrl } from './parseUrl';

const ACCEPTED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'data:', 'ftp:'];
/**
 * We should not allow these schemes by default as this opens the door to code injection. These
 * type of injections are "accepted" by the URL parser from the browser, so we need to make sure
 * they can't go through even when accepting any protocols.
 */
const FORBIDDEN_PROTOCOLS = ['javascript:'];

/**
 * Default max length for a url.
 * There is no strong rule. But some urls for embeds are quite large.
 * https://abramillar.com/2018/02/12/urls-maximum-length-long-best/
 * https://www.geeksforgeeks.org/maximum-length-of-a-url-in-different-browsers/
 */
const URL_MAX_LENGTH = 2048;

/**
 * Options when checking a url.
 */
export interface IsUrlOptions {
    /**
     * Should it accept any scheme (defaults to true)
     */
    allowAnyScheme?: boolean;
    /**
     * Should it accept `mailto:` (defaults to true)
     * Setting it to false, will also set `allowAnyScheme` to false
     */
    allowEmails?: boolean;
    /**
     * Should it accept `ftp:` (defaults to true)
     * Setting it to false, will also set `allowAnyScheme` to false
     */
    allowFtp?: boolean;
    /**
     * Should it accept `data:` (defaults to false)
     * Setting it to false, will also set `allowAnyScheme` to false
     */
    allowData?: boolean;
    /**
     * Should it accept urls without protocol (defaults to true)
     */
    allowNoProtocol?: boolean;
    /**
     * Maximum length for the url (defaults to 2048)
     */
    maxLength?: number;
}

/**
 * Check if a string is a URL to a website or email (and won't crash when used in `normalizeUrl`).
 *
 * All inputs returning true is accepted by normalizeUrl.
 */
export function isUrl(input: any, options: IsUrlOptions = {}): boolean {
    let {
        allowAnyScheme = true,
        allowEmails = true,
        allowFtp = true,
        allowData = true,
        allowNoProtocol = true,
        maxLength = URL_MAX_LENGTH,
    } = options;

    if (!allowFtp || !allowData || !allowEmails) {
        allowAnyScheme = false;
    }

    if (typeof input !== 'string') {
        return false;
    }

    const urlString = input.trim();

    // Quick check to early return
    if (urlString.includes(' ')) {
        return false;
    }
    if (urlString.length > maxLength) {
        return false;
    }

    let parsed = parseUrl(urlString);

    if (!parsed) {
        // If we can't parse it, it means there are no protocol indicated
        if (!allowNoProtocol) {
            return false;
        }

        // If the input looks like a path, we match the GitHub auto link logic.
        // Only input that looks like having a domain name and starts with "www." is considered a valid URL.
        //
        // For GitHub, auto link returns:
        //   - true for www.google.com/soemthing
        //   - false for google.com
        //   - false for docs.google.com
        if (
            !urlString.startsWith('//') &&
            (!urlString.match(/^([a-zA-Z0-9_]+)\.([a-zA-Z0-9_]+)/) || !urlString.startsWith('www.'))
        ) {
            return false;
        }
    }

    // Fallback to normalizing the url before parsing
    if (!parsed) {
        try {
            parsed = parseUrl(normalizeUrl(urlString));
        } catch (_err) {
            // normalizeUrl could fail if the url is not a valid url
        }
    }

    if (!parsed) {
        return false;
    }

    // Check if the protocol is accepted
    if (!allowAnyScheme) {
        if (!ACCEPTED_PROTOCOLS.includes(parsed.protocol)) {
            return false;
        }
        if (!allowEmails && parsed.protocol === 'mailto:') {
            return false;
        }
        if (!allowData && parsed.protocol === 'data:') {
            return false;
        }
        if (!allowFtp && parsed.protocol === 'ftp:') {
            return false;
        }
    } else if (allowAnyScheme && FORBIDDEN_PROTOCOLS.includes(parsed.protocol)) {
        return false;
    }

    return true;
}

/**
 * Check if the input looks like a URL, and prepend the https protocol
 * Rules
 * - Disallows spaces anywhere.
 * - Disallows < or >.
 * - Disallows scheme-looking prefixes (ie: javascript:).
 * - Domain must be 1–63 characters (RFC compliant).
 * - Only allows safe URL characters, no dangerous characters like " or ; outside their allowed context (RFC compliant).
 * - Enforces at least one "." in the domain.
 * - No protocol and no "www" required but allowed.
 */
export function isUrlLike(input: string): boolean {
    if (typeof input !== 'string') {
        return false;
    }

    const urlString = input.trim();

    if (
        !urlString ||
        urlString.includes(' ') ||
        urlString.includes('\n') ||
        urlString.length > URL_MAX_LENGTH
    ) {
        return false;
    }

    if (isUrl(urlString)) {
        return true;
    }

    return URL_LIKE_REGEX.test(urlString);
}
/**
 * Regex to evaluate if a string is a URL-like string without protocol.
 */
const URL_LIKE_REGEX =
    /^(?![a-zA-Z][a-zA-Z0-9+.-]*:)(?!.*[<>])([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9\-._~%!$&'()*+,;=:@/]*)?(\?[a-zA-Z0-9\-._~%!$&'()*+,;=:@/?]*)?$/;

/**
 * Return true if a url is an http: url.
 */
export function isHttpUrl(input: string): boolean {
    return isUrl(input, {
        allowAnyScheme: false,
        allowEmails: false,
        allowFtp: false,
        allowData: false,
        allowNoProtocol: false,
    });
}

/**
 * Return true if a url is a mailto: url.
 */
export function isMailToUrl(input: string): boolean {
    if (!isUrl(input)) {
        return false;
    }

    const parsed = parseUrl(input);
    return Boolean(parsed && parsed.protocol === 'mailto:');
}

/**
 * Return true if a url is a data url.
 */
export function isDataUrl(input: string): boolean {
    return Boolean(input.match(DATA_URL_REGEX));
}

/**
 * Parse a string that looks like a URL to a valid href.
 */
export function normalizeUrlLikeStringToUrl(input: any, options: IsUrlOptions = {}): string | null {
    let {
        allowAnyScheme = true,
        allowEmails = true,
        allowFtp = true,
        allowData = true,
        maxLength = URL_MAX_LENGTH,
    } = options;

    if (!allowFtp || !allowData || !allowEmails) {
        allowAnyScheme = false;
    }

    if (typeof input !== 'string') {
        return null;
    }

    const urlString = input.trim();

    // Quick check to early return
    if (urlString.includes(' ') || urlString.length > maxLength) {
        return null;
    }

    // Check if the input is a valid URL
    const parsed = parseUrl(urlString);

    if (parsed) {
        // Check if the protocol is accepted
        if (!allowAnyScheme) {
            if (!ACCEPTED_PROTOCOLS.includes(parsed.protocol)) {
                return null;
            }
            if (!allowEmails && parsed.protocol === 'mailto:') {
                return null;
            }
            if (!allowData && parsed.protocol === 'data:') {
                return null;
            }
            if (!allowFtp && parsed.protocol === 'ftp:') {
                return null;
            }
        } else if (allowAnyScheme && FORBIDDEN_PROTOCOLS.includes(parsed.protocol)) {
            return null;
        }

        return parsed.href;
    }

    if (isUrlLike(urlString)) {
        try {
            // Prepend the https protocol and normalize the url string
            const normalizedUrl = normalizeUrl(`https://${urlString}`);

            return normalizedUrl ?? null;
        } catch (_err) {
            // normalizeUrl could fail if the url is not a valid url
            return null;
        }
    }

    return null;
}
